import React, { useState, useEffect } from 'react';
import { formatCellValue } from '../utils/formatters';
import { apiGet } from '../../../shared/services/apiClient';
import { CSP_NONCE } from '../../../shared/services/cspNonce';

interface SchoolDetailsModalProps {
  schoolID: number;
  onClose: () => void;
  selectedCategory?: string;
  selectedKPI?: string;
  selectedKPILabel?: string;
  schoolKPIData?: any;
}

interface ContactDetail {
  id: number;
  role: number;
  roleName: string;
  name: string;
  contactNo: string;
  emailID: string;
}

interface SchoolData {
  schoolID: number;
  uuid: string;
  schoolName: string;
  roID: number;
  roName: string;
  contactNo: string;
  emailID: string;
  website: string;
  stationID: string;
  stationType: string;
  stateID: number;
  districtID: number;
  city: string;
  postalAddress1: string;
  postalAddress2: string;
  postalAddress3: string;
  pinCode: number;
  constituencyID: number;
  longitude: string;
  latitude: string;
  locationType: number;
  establishmentYear: string;
  udiseCode: string;
  cbseAffliation: string;
  cbseSchoolCode: string;
  sponsoredBy: number;
  sponsoringAgency: string;
  sponsoringAgencyQuota: number;
  uptoClass: number;
  sections: number;
  schoolBuilding: number;
  infraFacility: number;
  isPMShree: number;
  status: number;
  principalName: string;
  principalContactNo: string;
  contactDetails?: ContactDetail[];
  createdAt: string;
  updatedAt: string;
}

// Fields from infraData that should be excluded (they are basic school info shown in the header card)
const INFRA_EXCLUDED_FIELDS = new Set([
  'schoolID', 'school_id', 'id', 'kvID', 'uuid',
  'schoolName', 'state', 'region', 'isPmshri',
  'createdAt', 'updatedAt', 'status',
  // Enrollment-specific nested / non-display fields
  'classDetails', 'classWiseStudents', 'ClassWiseStudents',
  'genderDetails', 'socialDetails', 'minorityDetails',
  'School', 'RO', 'Gender', 'SocialCategory', 'MinorityCategory', 'ParentsServiceCategory',
  'PMShreePhase', 'label', 'tabLabel',
]);

const formatInfraLabel = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();

const formatInfraValue = (value: any): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const InfoCard: React.FC<{ label: string; value: string | number | undefined | null }> = ({ label, value }) => (
  <div style={{
    background: '#f8f4fc',
    border: '1px solid #e9d5f5',
    borderRadius: '10px',
    padding: '12px 16px',
  }}>
    <div style={{ fontSize: '11px', fontWeight: '600', color: '#9B59B6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
      {label}
    </div>
    <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d2d2d', wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

const InfraCard: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div style={{
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '10px',
    padding: '12px 16px',
    textAlign: 'center',
  }}>
    <div style={{ fontSize: '11px', fontWeight: '600', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
      {label}
    </div>
    <div style={{ fontSize: '20px', fontWeight: '700', color: '#0c4a6e' }}>
      {formatInfraValue(value)}
    </div>
  </div>
);

const SchoolDetailsModal: React.FC<SchoolDetailsModalProps> = ({
  schoolID,
  onClose,
  selectedCategory,
  schoolKPIData,
}) => {
  const [schoolData, setSchoolData] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchoolDetails = async () => {
      try {
        setLoading(true);
        const result = await apiGet(`/kvis/schools/${schoolID}`);
        if (result.success) {
          setSchoolData(result.data);
        } else {
          setError('Failed to load school details');
        }
      } catch (err) {
        console.error('Error fetching school details:', err);
        setError('Error loading school details');
      } finally {
        setLoading(false);
      }
    };
    fetchSchoolDetails();
  }, [schoolID]);

  // Infra-specific fields to render (filtered — skip excluded keys, empty/null values, and nested objects/arrays)
  const infraEntries = schoolKPIData
    ? Object.entries(schoolKPIData).filter(
        ([key, val]) =>
          !INFRA_EXCLUDED_FIELDS.has(key) &&
          val !== null &&
          val !== undefined &&
          val !== '' &&
          typeof val !== 'object'   // skip nested arrays/objects (e.g. classDetails, Gender)
      )
    : [];

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white', borderRadius: '16px',
          maxWidth: '860px', width: '100%', maxHeight: '90vh',
          overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg, #481789 0%, #6B1786 100%)',
          color: 'white', padding: '20px 28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: '16px',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', lineHeight: 1.3 }}>
              {schoolData?.schoolName || schoolKPIData?.schoolName || 'School Details'}
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {(schoolKPIData?.state || schoolKPIData?.region) && (
                <span style={{ fontSize: '13px', opacity: 0.9 }}>
                  📍 {[schoolKPIData.state, schoolKPIData.region].filter(Boolean).join(' • ')}
                </span>
              )}
              {schoolData?.isPMShree === 1 || schoolKPIData?.isPmshri ? (
                <span style={{
                  background: 'rgba(255,255,255,0.25)', padding: '2px 10px',
                  borderRadius: '10px', fontSize: '12px', fontWeight: '600',
                }}>⭐ PM SHRI School</span>
              ) : (
                <span style={{
                  background: 'rgba(255,255,255,0.12)', padding: '2px 10px',
                  borderRadius: '10px', fontSize: '12px', color: 'rgba(255,255,255,0.7)',
                }}>Non PM SHRI</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <button
              onClick={() => window.open(`/kvs-samagam/school/${schoolID}`, '_blank')}
              style={{
                background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.6)',
                color: 'white', padding: '8px 16px', borderRadius: '8px',
                cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                whiteSpace: 'nowrap', transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.28)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.18)')}
            >
              View Full Details ↗
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
                width: '36px', height: '36px', borderRadius: '50%',
                cursor: 'pointer', fontSize: '22px', fontWeight: 'bold',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s', flexShrink: 0,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.28)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
            >
              ×
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div style={{
                width: '40px', height: '40px',
                border: '4px solid #f3f3f3', borderTop: '4px solid #6B1786',
                borderRadius: '50%', margin: '0 auto 16px',
                animation: 'spin 1s linear infinite',
              }} />
              Loading school details...
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              background: '#fff3f3', border: '1px solid #ffcdd2',
              color: '#c62828', padding: '16px', borderRadius: '8px', textAlign: 'center',
            }}>
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {/* ── School Info Card ── */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '14px', fontWeight: '700', color: '#6A1786',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  🏫 School Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  <InfoCard label="Region" value={schoolData?.roName || schoolKPIData?.region} />
                  <InfoCard label="State" value={schoolKPIData?.state || schoolData?.city} />
                  <InfoCard label="PM SHRI" value={
                    schoolData?.isPMShree === 1 || schoolKPIData?.isPmshri ? '✅ Yes' : '❌ No'
                  } />
                  <InfoCard label="Principal Name" value={schoolData?.principalName} />
                  <InfoCard label="Principal Contact" value={schoolData?.principalContactNo || schoolData?.contactNo} />
                  <InfoCard label="Email" value={schoolData?.emailID} />
                  <InfoCard label="Established" value={schoolData?.establishmentYear} />
                </div>
              </div>

              {/* ── Tab-specific Infrastructure Details ── */}
              {infraEntries.length > 0 && (
                <div>
                  <h3 style={{
                    fontSize: '14px', fontWeight: '700', color: '#0369a1',
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                    📊 {selectedCategory ? `${selectedCategory} Details` : 'Infrastructure Details'}
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.min(infraEntries.length, 3)}, 1fr)`,
                    gap: '10px',
                  }}>
                    {infraEntries.map(([key, value]) => (
                      <InfraCard key={key} label={formatInfraLabel(key)} value={value} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style nonce={CSP_NONCE}>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SchoolDetailsModal;
